import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getDoctorAppointments,
  submitConsultation,
  uploadConsultationDocument,
} from '../services/consultationApi';

const DOC_TYPES = ['SCAN', 'REPORT', 'PRESCRIPTION'];
const DOC_ICONS = { SCAN: '🔬', REPORT: '📋', PRESCRIPTION: '💊' };
const DOC_COLORS = {
  SCAN:         { color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
  REPORT:       { color: '#4f46e5', bg: '#eef2ff', border: '#c7d2fe' },
  PRESCRIPTION: { color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
};

/* ── small reusable textarea field ── */
const Field = ({ label, value, onChange, placeholder, rows = 3 }) => (
  <div style={{ marginBottom: 20 }}>
    <label style={{
      display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280',
      textTransform: 'uppercase', letterSpacing: '0.08em',
      fontFamily: 'DM Sans, sans-serif', marginBottom: 7,
    }}>
      {label}
    </label>
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%', borderRadius: 12, border: '1.5px solid #e5e7eb',
        padding: '11px 13px', fontSize: 13.5, color: '#1e1b4b',
        fontFamily: 'DM Sans, sans-serif', resize: 'vertical',
        lineHeight: 1.6, outline: 'none', transition: 'border .12s',
        boxSizing: 'border-box', background: '#fafafa',
      }}
      onFocus={e => e.target.style.border = '1.5px solid #a5b4fc'}
      onBlur={e  => e.target.style.border = '1.5px solid #e5e7eb'}
    />
  </div>
);

/* ── section header ── */
const SectionLabel = ({ children }) => (
  <div style={{
    fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase',
    letterSpacing: '0.08em', fontFamily: 'DM Sans, sans-serif', marginBottom: 10,
  }}>
    {children}
  </div>
);

const ConsultationFormPage = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  /* ── appointment header info ── */
  const [appt, setAppt]               = useState(null);
  const [apptLoading, setApptLoading] = useState(true);

  /* ── consultation fields ── */
  const [patientAttended, setPatientAttended]       = useState(true);
  const [existingConditions, setExistingConditions] = useState('');
  const [analysis, setAnalysis]                     = useState('');
  const [result, setResult]                         = useState('');

  /* ── medicines ── */
  const [medicines, setMedicines] = useState([
    { name: '', dosage: '', durationDays: '', instructions: '' },
  ]);

  const addMedicine    = () => setMedicines(m => [...m, { name: '', dosage: '', durationDays: '', instructions: '' }]);
  const removeMedicine = (i) => setMedicines(m => m.filter((_, idx) => idx !== i));
  const updateMed      = (i, field, val) => setMedicines(m => m.map((med, idx) => idx === i ? { ...med, [field]: val } : med));

  /* ── document rows ── */
  // each row: { id, docType, file: File|null, note: string }
  // note is a local display-only annotation — it is NEVER uploaded to S3
  const [docRows, setDocRows] = useState([
    { id: 1, docType: 'REPORT', file: null, note: '' },
  ]);
  const nextId   = useRef(2);
  const fileRefs = useRef({});

  /* ── submit state ── */
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState(null);

  /* ── load appointment for header ── */
  const loadAppt = useCallback(async () => {
    setApptLoading(true);
    try {
      const data = await getDoctorAppointments();
      setAppt(data.find(a => String(a.id) === String(appointmentId)) || null);
    } catch {
      setAppt(null);
    } finally {
      setApptLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => { loadAppt(); }, [loadAppt]);

  /* ── doc row helpers ── */
  const addDocRow = () => {
    setDocRows(prev => [...prev, { id: nextId.current++, docType: 'REPORT', file: null, note: '' }]);
  };

  const removeDocRow = (id) => {
    setDocRows(prev => prev.filter(r => r.id !== id));
  };

  const updateRow = (id, patch) => {
    setDocRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  };

  /* ── submit: save consultation record + upload only real files ── */
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // 1. Submit consultation record (text fields + medicines)
      await submitConsultation(appointmentId, {
        patientAttended,
        existingConditions: existingConditions || null,
        analysis: analysis || null,
        result: result || null,
        medicines: patientAttended
          ? medicines
              .filter(m => m.name.trim())
              .map(m => ({
                name: m.name.trim(),
                dosage: m.dosage || null,
                durationDays: m.durationDays ? Number(m.durationDays) : null,
                instructions: m.instructions || null,
              }))
          : [],
      });

      // 2. Upload only rows that have a real file attached.
      //    Notes are display-only and intentionally NOT uploaded.
      const uploads = docRows
        .filter(r => r.file)
        .map(r => uploadConsultationDocument(appointmentId, r.file, r.docType));

      await Promise.allSettled(uploads); // don't block navigation if an upload partially fails
      navigate('/doctor/dashboard');
    } catch (e) {
      setSubmitError(e?.response?.data?.message || 'Submission failed. Please try again.');
      setSubmitting(false);
    }
  };

  const dateStr = appt?.appointmentDate
    ? new Date(appt.appointmentDate).toLocaleDateString(undefined, {
        weekday: 'long', month: 'long', day: 'numeric',
      })
    : '';

  return (
    <div className="page">
      <main className="page-main" style={{ maxWidth: '100%', padding: '24px 24px 40px' }}>

        {/* Back */}
        <button
          onClick={() => navigate('/doctor/dashboard')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, fontWeight: 600, color: '#6b7280',
            background: 'none', border: 'none', cursor: 'pointer',
            marginBottom: 20, padding: 0,
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#4f46e5'}
          onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
        >
          ← Back to Dashboard
        </button>

        {/* Header */}
        <div className="fade-up" style={{ marginBottom: '1.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 50, height: 50, borderRadius: 16, flexShrink: 0,
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
            }}>
              📝
            </div>
            <div>
              <h1 className="t-h1" style={{ marginBottom: 3, fontSize: 21 }}>Submit Consultation</h1>
              {apptLoading
                ? <div className="skeleton skeleton-line" style={{ width: 240, height: 12 }} />
                : appt
                  ? <p className="t-body-sm">
                      {appt.patientName || 'Patient'}
                      {appt.age    ? ` · ${appt.age} yrs`  : ''}
                      {appt.gender ? ` · ${appt.gender.charAt(0) + appt.gender.slice(1).toLowerCase()}` : ''}
                      {dateStr     ? ` · 📅 ${dateStr}`    : ''}
                      {appt.slotTime ? ` · 🕐 ${appt.slotTime}` : ''}
                    </p>
                  : <p className="t-body-sm">Appointment #{appointmentId}</p>
              }
            </div>
          </div>
        </div>

        {/* ── Two-column grid (stacks on mobile) ── */}
        <div
          className="fade-up-1"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 20,
            alignItems: 'start',
          }}
        >

          {/* ── Left column: clinical fields ── */}
          <div className="card" style={{ padding: '28px 28px 24px' }}>

            {/* Attended toggle */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', borderRadius: 12, marginBottom: 26,
              background: patientAttended ? '#ecfdf5' : '#fef2f2',
              border: `1px solid ${patientAttended ? '#6ee7b7' : '#fca5a5'}`,
            }}>
              <span style={{
                fontSize: 13, fontWeight: 700, fontFamily: 'DM Sans, sans-serif',
                color: patientAttended ? '#059669' : '#dc2626',
              }}>
                {patientAttended ? '✅ Patient Attended' : '🚫 No-Show'}
              </span>
              <div
                onClick={() => setPatientAttended(v => !v)}
                style={{
                  width: 42, height: 24, borderRadius: 12,
                  background: patientAttended ? '#059669' : '#d1d5db',
                  position: 'relative', cursor: 'pointer', transition: 'background .2s', flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute', top: 4, left: patientAttended ? 22 : 4,
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
            </div>

            <Field
              label="Existing Conditions"
              value={existingConditions}
              onChange={setExistingConditions}
              placeholder="e.g. Hypertension, Type 2 Diabetes, Asthma…"
            />
            <Field
              label="Doctor's Analysis"
              value={analysis}
              onChange={setAnalysis}
              placeholder="Clinical observations, findings, diagnostic impressions…"
            />
            <Field
              label="Outcome / Result"
              value={result}
              onChange={setResult}
              placeholder="Treatment plan, recommendations, follow-up instructions…"
            />

            {/* Medicines section — only when attended */}
            {patientAttended && (
              <div style={{ marginBottom: 20 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10,
                }}>
                  <label style={{
                    fontSize: 11, fontWeight: 700, color: '#6b7280',
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    fontFamily: 'DM Sans, sans-serif',
                  }}>
                    Prescribed Medicines
                  </label>
                  <button
                    onClick={addMedicine}
                    style={{
                      fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 9999,
                      background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe',
                      cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                    }}
                  >
                    + Add Medicine
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {medicines.map((med, i) => (
                    <div key={i} style={{
                      border: '1.5px solid #e5e7eb', borderRadius: 12,
                      padding: '14px 16px', background: '#fafafa',
                    }}>
                      {/* Row header */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif' }}>
                          Medicine #{i + 1}
                        </span>
                        {medicines.length > 1 && (
                          <button
                            onClick={() => removeMedicine(i)}
                            style={{
                              fontSize: 12, color: '#9ca3af', background: 'none',
                              border: 'none', cursor: 'pointer', padding: '2px 6px',
                              borderRadius: 6, fontFamily: 'DM Sans, sans-serif',
                            }}
                            onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                            onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
                          >
                            ✕ Remove
                          </button>
                        )}
                      </div>

                      {/* Name */}
                      <label style={{
                        display: 'block', fontSize: 11, fontWeight: 600, color: '#9ca3af',
                        fontFamily: 'DM Sans, sans-serif', marginBottom: 4,
                      }}>
                        Medicine name <span className="required-star">*</span>
                      </label>
                      <input
                        value={med.name}
                        onChange={e => updateMed(i, 'name', e.target.value)}
                        placeholder="e.g. Amlodipine 5mg"
                        style={{
                          width: '100%', borderRadius: 9, border: '1.5px solid #e5e7eb',
                          padding: '9px 11px', fontSize: 13, color: '#1e1b4b',
                          fontFamily: 'DM Sans, sans-serif', outline: 'none',
                          boxSizing: 'border-box', background: '#fff', marginBottom: 8,
                        }}
                        onFocus={e => e.target.style.border = '1.5px solid #a5b4fc'}
                        onBlur={e  => e.target.style.border = '1.5px solid #e5e7eb'}
                      />

                      {/* Dosage + Duration */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                        <input
                          value={med.dosage}
                          onChange={e => updateMed(i, 'dosage', e.target.value)}
                          placeholder="Dosage e.g. 1 tab daily"
                          style={{
                            borderRadius: 9, border: '1.5px solid #e5e7eb',
                            padding: '9px 11px', fontSize: 13, color: '#1e1b4b',
                            fontFamily: 'DM Sans, sans-serif', outline: 'none', background: '#fff',
                          }}
                          onFocus={e => e.target.style.border = '1.5px solid #a5b4fc'}
                          onBlur={e  => e.target.style.border = '1.5px solid #e5e7eb'}
                        />
                        <input
                          type="number"
                          min="1"
                          value={med.durationDays}
                          onChange={e => updateMed(i, 'durationDays', e.target.value)}
                          placeholder="Duration (days)"
                          style={{
                            borderRadius: 9, border: '1.5px solid #e5e7eb',
                            padding: '9px 11px', fontSize: 13, color: '#1e1b4b',
                            fontFamily: 'DM Sans, sans-serif', outline: 'none', background: '#fff',
                          }}
                          onFocus={e => e.target.style.border = '1.5px solid #a5b4fc'}
                          onBlur={e  => e.target.style.border = '1.5px solid #e5e7eb'}
                        />
                      </div>

                      {/* Instructions */}
                      <input
                        value={med.instructions}
                        onChange={e => updateMed(i, 'instructions', e.target.value)}
                        placeholder="Instructions e.g. Take after meals"
                        style={{
                          width: '100%', borderRadius: 9, border: '1.5px solid #e5e7eb',
                          padding: '9px 11px', fontSize: 13, color: '#1e1b4b',
                          fontFamily: 'DM Sans, sans-serif', outline: 'none',
                          boxSizing: 'border-box', background: '#fff',
                        }}
                        onFocus={e => e.target.style.border = '1.5px solid #a5b4fc'}
                        onBlur={e  => e.target.style.border = '1.5px solid #e5e7eb'}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {submitError && (
              <div style={{
                marginTop: 4, padding: '12px 16px', borderRadius: 12,
                background: '#fef2f2', border: '1px solid #fca5a5',
                color: '#dc2626', fontSize: 13, fontFamily: 'DM Sans, sans-serif',
              }}>
                ⚠️ {submitError}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
              <button
                onClick={() => navigate('/doctor/dashboard')}
                disabled={submitting}
                style={{
                  flex: 1, padding: '13px 0', borderRadius: 12,
                  border: '1.5px solid #e5e7eb', background: '#fff',
                  color: '#374151', fontSize: 14, fontWeight: 600,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: 'DM Sans, sans-serif', transition: 'background .12s',
                }}
                onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  flex: 2, padding: '13px 0', borderRadius: 12, border: 'none',
                  background: submitting
                    ? '#c7d2fe'
                    : 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                  color: '#fff', fontSize: 14, fontWeight: 700,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: 'DM Sans, sans-serif', transition: 'opacity .12s',
                  letterSpacing: '0.01em',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {submitting
                  ? <><span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #fff', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} /> Submitting…</>
                  : '✓ Submit Consultation'
                }
              </button>
            </div>
          </div>

          {/* ── Right column: documents ── */}
          <div className="card" style={{ padding: '28px 28px 24px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 16,
            }}>
              <SectionLabel>Documents (Optional)</SectionLabel>
              <button
                onClick={addDocRow}
                style={{
                  fontSize: 12, fontWeight: 700, padding: '5px 13px', borderRadius: 9999,
                  background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe',
                  cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                  display: 'flex', alignItems: 'center', gap: 5,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#4f46e5'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.color = '#4f46e5'; }}
              >
                + Add Document
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {docRows.map((row, idx) => {
                const c = DOC_COLORS[row.docType];
                return (
                  <div key={row.id} style={{
                    border: `1.5px solid ${row.file ? c.border : '#e5e7eb'}`,
                    borderRadius: 14, padding: '16px 18px',
                    background: row.file ? c.bg : '#fafafa',
                    transition: 'all .15s',
                  }}>
                    {/* Row header: type selector + remove */}
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif', flexShrink: 0 }}>
                        #{idx + 1}
                      </span>
                      {DOC_TYPES.map(t => {
                        const tc = DOC_COLORS[t];
                        const active = row.docType === t;
                        return (
                          <button
                            key={t}
                            onClick={() => updateRow(row.id, { docType: t })}
                            style={{
                              padding: '5px 12px', borderRadius: 9999, fontSize: 11.5,
                              fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                              transition: 'all .1s',
                              border: active ? `2px solid ${tc.border}` : '1.5px solid #e5e7eb',
                              background: active ? tc.bg : '#fff',
                              color: active ? tc.color : '#9ca3af',
                            }}
                          >
                            {DOC_ICONS[t]} {t}
                          </button>
                        );
                      })}
                      {docRows.length > 1 && (
                        <button
                          onClick={() => removeDocRow(row.id)}
                          style={{
                            marginLeft: 'auto', fontSize: 13, color: '#9ca3af',
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontFamily: 'DM Sans, sans-serif', padding: '4px 8px',
                            borderRadius: 8, transition: 'color .1s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                          onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
                        >
                          ✕ Remove
                        </button>
                      )}
                    </div>

                    {/* File picker */}
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '11px 14px', borderRadius: 10, marginBottom: 10,
                      border: `1.5px dashed ${row.file ? c.border : '#d1d5db'}`,
                      background: row.file ? '#fff' : '#f9fafb',
                      cursor: 'pointer', transition: 'all .12s',
                    }}>
                      <span style={{ fontSize: 18, flexShrink: 0 }}>📎</span>
                      <span style={{
                        flex: 1, fontSize: 13, fontWeight: 600,
                        color: row.file ? c.color : '#9ca3af',
                        fontFamily: 'DM Sans, sans-serif',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {row.file ? row.file.name : 'Attach a file (optional)'}
                      </span>
                      {row.file && (
                        <button
                          onClick={e => { e.preventDefault(); updateRow(row.id, { file: null }); if (fileRefs.current[row.id]) fileRefs.current[row.id].value = ''; }}
                          style={{
                            fontSize: 12, color: '#9ca3af', background: 'none',
                            border: 'none', cursor: 'pointer', flexShrink: 0, padding: 2,
                          }}
                        >
                          ✕
                        </button>
                      )}
                      <input
                        ref={el => fileRefs.current[row.id] = el}
                        type="file"
                        style={{ display: 'none' }}
                        onChange={e => updateRow(row.id, { file: e.target.files[0] || null })}
                      />
                    </label>

                    {/* Text note — local only, never uploaded */}
                    <textarea
                      value={row.note}
                      onChange={e => updateRow(row.id, { note: e.target.value })}
                      placeholder="Add a note (optional) — visible here only, not uploaded to cloud…"
                      rows={2}
                      style={{
                        width: '100%', borderRadius: 10, border: '1.5px solid #e5e7eb',
                        padding: '9px 12px', fontSize: 13, color: '#1e1b4b',
                        fontFamily: 'DM Sans, sans-serif', resize: 'vertical',
                        lineHeight: 1.55, outline: 'none', transition: 'border .12s',
                        boxSizing: 'border-box',
                        background: row.note.trim() ? '#fff' : '#f9fafb',
                      }}
                      onFocus={e => e.target.style.border = '1.5px solid #a5b4fc'}
                      onBlur={e  => e.target.style.border = '1.5px solid #e5e7eb'}
                    />
                    {/* Hint shown only when there's a note but no file */}
                    {row.note.trim() && !row.file && (
                      <div style={{
                        marginTop: 6, fontSize: 11.5, color: '#9ca3af',
                        fontFamily: 'DM Sans, sans-serif',
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        💬 This note is for your reference only and will not be stored on the server.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ConsultationFormPage;